// Gestão de Utilizadores de Parceria
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit, Trash2, Users, Shield, User, Key } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const GestaoUtilizadoresParceria = ({ user }) => {
  const [utilizadores, setUtilizadores] = useState([]);
  const [parcerias, setParcerias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [novaPassword, setNovaPassword] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    nome: '',
    email: '',
    password: '',
    role: 'colaborador_parceria',
    parceria_id: ''
  });

  const isAdmin = user?.role === 'admin';
  const isResponsavel = user?.role === 'responsavel';
  const isAdminParceria = user?.role === 'admin_parceria';
  const canCreateAdminParceria = isAdmin || isResponsavel;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Carregar parcerias (se Admin/Responsável Janeira)
      if (canCreateAdminParceria) {
        const parceriasRes = await api.get('/parcerias/entidades');
        setParcerias(parceriasRes.data);
      }

      // Carregar utilizadores
      const utilizadoresRes = await api.get('/parcerias/utilizadores');
      setUtilizadores(utilizadoresRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setFormData({
      username: '',
      nome: '',
      email: '',
      password: '',
      role: isAdminParceria ? 'colaborador_parceria' : 'admin_parceria',
      parceria_id: isAdminParceria ? user.parceria_id : ''
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.username.trim() || !formData.nome.trim() || !formData.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (!formData.parceria_id) {
      toast.error('Selecione uma parceria');
      return;
    }

    try {
      await api.post('/parcerias/utilizadores', formData);
      toast.success('Utilizador criado com sucesso');
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Erro ao criar utilizador:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar utilizador');
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;
    
    try {
      await api.delete(`/parcerias/utilizadores/${showDeleteConfirm.id}`);
      toast.success('Utilizador eliminado com sucesso');
      setShowDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar utilizador');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin_parceria':
        return <Badge className="bg-purple-100 text-purple-700"><Shield className="h-3 w-3 mr-1" />Admin Parceria</Badge>;
      case 'colaborador_parceria':
        return <Badge variant="outline"><User className="h-3 w-3 mr-1" />Colaborador</Badge>;
      default:
        return <Badge>{role}</Badge>;
    }
  };

  const getParceriaNome = (parceriaId) => {
    const parceria = parcerias.find(p => p.id === parceriaId);
    return parceria?.nome_entidade || 'N/A';
  };

  const handleResetPassword = async () => {
    if (!showResetPassword || !novaPassword) {
      toast.error('Digite a nova password');
      return;
    }
    if (novaPassword.length < 8) {
      toast.error('Password deve ter no mínimo 8 caracteres');
      return;
    }
    
    try {
      await api.put(`/parcerias/utilizadores/${showResetPassword.id}/reset-password`, {
        nova_password: novaPassword
      });
      toast.success('Password alterada com sucesso');
      setShowResetPassword(null);
      setNovaPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar password');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gestao-utilizadores-parceria">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Utilizadores de Parceria</h2>
          <p className="text-sm text-muted-foreground">
            {isAdminParceria 
              ? 'Gerir colaboradores da sua parceria'
              : 'Gerir admins e colaboradores de todas as parcerias'
            }
          </p>
        </div>
        <Button onClick={handleOpenModal} data-testid="btn-novo-utilizador">
          <Plus className="h-4 w-4 mr-2" />
          Novo Utilizador
        </Button>
      </div>

      {/* Lista */}
      <Card>
        <CardContent className="pt-6">
          {utilizadores.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum utilizador de parceria configurado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Perfil</TableHead>
                  {canCreateAdminParceria && <TableHead>Parceria</TableHead>}
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {utilizadores.map(utilizador => (
                  <TableRow key={utilizador.id}>
                    <TableCell className="font-medium">{utilizador.username}</TableCell>
                    <TableCell>{utilizador.nome}</TableCell>
                    <TableCell>{utilizador.email || '-'}</TableCell>
                    <TableCell>{getRoleBadge(utilizador.role)}</TableCell>
                    {canCreateAdminParceria && (
                      <TableCell>{getParceriaNome(utilizador.parceria_id)}</TableCell>
                    )}
                    <TableCell>
                      <Badge variant={utilizador.ativo ? 'success' : 'secondary'}>
                        {utilizador.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          title="Alterar Password"
                          onClick={() => setShowResetPassword(utilizador)}
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600"
                          onClick={() => setShowDeleteConfirm(utilizador)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Utilizador de Parceria</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Username *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="utilizador123"
                />
              </div>
              <div>
                <Label>Nome *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome completo"
                />
              </div>
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@exemplo.com"
              />
            </div>

            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
              />
            </div>

            {canCreateAdminParceria && (
              <>
                <div>
                  <Label>Perfil</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin_parceria">Admin de Parceria</SelectItem>
                      <SelectItem value="colaborador_parceria">Colaborador de Parceria</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Parceria *</Label>
                  <Select 
                    value={formData.parceria_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, parceria_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma parceria..." />
                    </SelectTrigger>
                    <SelectContent>
                      {parcerias.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.nome_entidade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              Criar Utilizador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de Eliminação */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Utilizador</DialogTitle>
            <DialogDescription>
              Tem a certeza que deseja eliminar o utilizador "{showDeleteConfirm?.nome}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password */}
      <Dialog open={!!showResetPassword} onOpenChange={() => { setShowResetPassword(null); setNovaPassword(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Password</DialogTitle>
            <DialogDescription>
              Definir nova password para "{showResetPassword?.nome}" ({showResetPassword?.username})
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Nova Password</Label>
            <Input
              type="password"
              value={novaPassword}
              onChange={(e) => setNovaPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              data-testid="input-nova-password"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Deve incluir maiúsculas, minúsculas, números e símbolos
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowResetPassword(null); setNovaPassword(''); }}>
              Cancelar
            </Button>
            <Button onClick={handleResetPassword} disabled={novaPassword.length < 8}>
              Alterar Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestaoUtilizadoresParceria;
